import React, { useState, useEffect } from "react";
import { MdSchool,  MdFilterList } from "react-icons/md";
import MemoireServiceInstance from "service/memoire.service";
import ActionBar from "components/action-bar";
import MemoireTable from "./components/MemoireTable";
import MemoireFormModal from "./components/MemoireFormModal";
import DeleteConfirmationModal from "components/delete-confirm-modal";
import MemoireFilters from "./components/MemoireFilters";
import MemoireDetailModal from "./components/MemoireDetailModal";
import HeaderSection from "components/headers-section";

const MemoireManager = () => {
  const [memoires, setMemoires] = useState([]);
  const [filteredMemoires, setFilteredMemoires] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [currentMemoire, setCurrentMemoire] = useState({
    memoire_theme: "",
    memoire_description: "",
    memoire_date: new Date().toISOString().split('T')[0],
    memoire_annee: new Date().getFullYear(),
    memoire_etudiant: "",
    memoire_encadrant: "",
    memoire_filiere: "",
    memoire_etablissement: "",
    file: null
  });
  const [isEditing, setIsEditing] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    annee: "",
    filiere: "",
    etablissement: "",
    dateStart: null,
    dateEnd: null,
  });

  useEffect(() => {
    fetchMemoires();
  }, []);

  useEffect(() => {
    filterMemoires();
  }, [searchTerm, memoires, filters]);

  const fetchMemoires = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await MemoireServiceInstance.getAll();
      setMemoires(result || []);
      setFilteredMemoires(result || []);
    } catch (error) {
      setError(
        "Impossible de charger les mémoires. Veuillez réessayer plus tard."
      );
      console.error("Error fetching memoires:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterMemoires = () => {
    let filtered = [...memoires];
    
    // Recherche textuelle
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(memoire => 
        memoire.memoire_theme.toLowerCase().includes(search) ||
        memoire.memoire_description.toLowerCase().includes(search) ||
        memoire.memoire_etudiant.toLowerCase().includes(search) ||
        memoire.memoire_encadrant.toLowerCase().includes(search)
      );
    }
    
    // Filtre par année
    if (filters.annee) {
      filtered = filtered.filter(memoire => 
        memoire.memoire_annee.toString() === filters.annee.toString()
      );
    }
    
    // Filtre par filière
    if (filters.filiere) {
      filtered = filtered.filter(memoire => 
        memoire.memoire_filiere.toLowerCase().includes(filters.filiere.toLowerCase())
      );
    }
    
    // Filtre par établissement
    if (filters.etablissement) {
      filtered = filtered.filter(memoire => 
        memoire.memoire_etablissement.toLowerCase().includes(filters.etablissement.toLowerCase())
      );
    }
    
    // Filtre par date de début
    if (filters.dateStart) {
      filtered = filtered.filter(memoire => 
        new Date(memoire.memoire_date) >= new Date(filters.dateStart)
      );
    }
    
    // Filtre par date de fin
    if (filters.dateEnd) {
      filtered = filtered.filter(memoire => 
        new Date(memoire.memoire_date) <= new Date(filters.dateEnd)
      );
    }
    
    setFilteredMemoires(filtered);
  };

  const openCreateModal = () => {
    setCurrentMemoire({
      memoire_theme: "",
      memoire_description: "",
      memoire_date: new Date().toISOString().split('T')[0],
      memoire_annee: new Date().getFullYear(),
      memoire_etudiant: "",
      memoire_encadrant: "",
      memoire_filiere: "",
      memoire_etablissement: "",
      file: null
    });
    setIsEditing(false);
    setIsFormModalOpen(true);
  };

  const openEditModal = (memoire) => {
    setCurrentMemoire({
      memoire_id: memoire.memoire_id,
      memoire_theme: memoire.memoire_theme,
      memoire_description: memoire.memoire_description,
      memoire_date: memoire.memoire_date,
      memoire_annee: memoire.memoire_annee,
      memoire_etudiant: memoire.memoire_etudiant,
      memoire_encadrant: memoire.memoire_encadrant,
      memoire_filiere: memoire.memoire_filiere,
      memoire_etablissement: memoire.memoire_etablissement,
      memoire_fichier: memoire.memoire_fichier,
      file: null // Le fichier existant n'est pas modifié sauf si on en sélectionne un nouveau
    });
    setIsEditing(true);
    setIsFormModalOpen(true);
  };

  const openDeleteModal = (memoire) => {
    setCurrentMemoire(memoire);
    setIsDeleteModalOpen(true);
  };

  const openDetailModal = (memoire) => {
    setCurrentMemoire(memoire);
    setIsDetailModalOpen(true);
  };

  const handleSubmitForm = async (formData) => {
    setLoading(true);
    try {
      const data = new FormData();
      
      // Ajouter tous les champs au formData
      Object.keys(formData).forEach(key => {
        if (key !== 'file' || formData[key] !== null) {
          data.append(key, formData[key]);
        }
      });

      if (isEditing) {
        await MemoireServiceInstance.update(
          currentMemoire.memoire_id,
          data
        );
      } else {
        await MemoireServiceInstance.create(data);
      }
      
      fetchMemoires();
      setIsFormModalOpen(false);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        errors: error.response?.data?.errors || {
          general: isEditing 
            ? "Impossible de modifier le mémoire. Veuillez réessayer."
            : "Impossible de créer le mémoire. Veuillez réessayer."
        }
      };
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await MemoireServiceInstance.delete(currentMemoire.memoire_id);
      fetchMemoires();
      setIsDeleteModalOpen(false);
    } catch (error) {
      setError("Impossible de supprimer le mémoire. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const downloadMemoire = async (memoire) => {
    try {
      await MemoireServiceInstance.download(memoire.memoire_id);
    } catch (error) {
      console.error("Erreur lors du téléchargement:", error);
      setError("Impossible de télécharger le fichier. Veuillez réessayer.");
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters({ ...filters, ...newFilters });
  };

  const resetFilters = () => {
    setFilters({
      annee: "",
      filiere: "",
      etablissement: "",
      dateStart: null,
      dateEnd: null,
    });
    setSearchTerm("");
  };

  // Extraire des statistiques
  const getStats = () => {
    const totalMemoires = filteredMemoires.length;
    
    const uniqueYears = [...new Set(filteredMemoires.map(m => m.memoire_annee))].length;
    
    const uniqueFilieres = [...new Set(filteredMemoires.map(m => m.memoire_filiere))].length;
    
    const uniqueEtablissements = [...new Set(filteredMemoires.map(m => m.memoire_etablissement))].length;
    
    return { totalMemoires, uniqueYears, uniqueFilieres, uniqueEtablissements };
  };

  const stats = getStats();

  return (
    <div className="px-5 py-8 md:px-8 bg-gray-50 min-h-screen">
        <HeaderSection
          title="Gestion des Mémoires et Thèses"
          description="Consultez et gérez la base de données des mémoires et thèses"
          accentColor="#5850EC"
        />

        {/* Statistiques */}
        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-4">
          <div className="rounded-md bg-indigo-50 p-4 border border-indigo-200">
            <h3 className="text-sm font-medium text-indigo-800">Total des mémoires</h3>
            <p className="mt-2 text-xl font-bold text-indigo-700">{stats.totalMemoires}</p>
          </div>
          
          <div className="rounded-md bg-blue-50 p-4 border border-blue-200">
            <h3 className="text-sm font-medium text-blue-800">Années académiques</h3>
            <p className="mt-2 text-xl font-bold text-blue-700">{stats.uniqueYears}</p>
          </div>
          
          <div className="rounded-md bg-green-50 p-4 border border-green-200">
            <h3 className="text-sm font-medium text-green-800">Filières</h3>
            <p className="mt-2 text-xl font-bold text-green-700">{stats.uniqueFilieres}</p>
          </div>
          
          <div className="rounded-md bg-amber-50 p-4 border border-amber-200">
            <h3 className="text-sm font-medium text-amber-800">Établissements</h3>
            <p className="mt-2 text-xl font-bold text-amber-700">{stats.uniqueEtablissements}</p>
          </div>
        </div>

        <ActionBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Rechercher par thème, étudiant ou encadrant..."
          searchIcon={MdSchool}
          onRefresh={fetchMemoires}
          onCreateNew={openCreateModal}
          buttonLabel="Nouveau Mémoire"
          loading={loading}
          primaryColor="#5850EC"
          hoverColor="#4F46E5"
          customActions={
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 rounded-md bg-gray-100 px-3 py-2 text-gray-700 hover:bg-gray-200 transition-colors"
            >
              <MdFilterList className="h-5 w-5" />
              <span>Filtres</span>
              <span className="rounded-full bg-gray-200 px-2 py-0.5 text-xs">
                {Object.values(filters).filter(v => v !== null && v !== "").length}
              </span>
            </button>
          }
        />

        {showFilters && (
          <MemoireFilters 
            filters={filters}
            onFilterChange={handleFilterChange}
            onResetFilters={resetFilters}
          />
        )}

        {error && (
          <div
            className="mb-6 rounded-lg bg-red-50 p-4 text-sm text-red-600 border border-red-200"
            role="alert"
          >
            {error}
          </div>
        )}

        <MemoireTable
          memoires={filteredMemoires}
          loading={loading}
          onEdit={openEditModal}
          onDelete={openDeleteModal}
          onView={openDetailModal}
          onDownload={downloadMemoire}
        />

      <MemoireFormModal
        isOpen={isFormModalOpen}
        onClose={() => setIsFormModalOpen(false)}
        onSubmit={handleSubmitForm}
        initialMemoire={currentMemoire}
        isEditing={isEditing}
        loading={loading}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        item={currentMemoire}
        itemName={currentMemoire.memoire_theme}
        itemType="mémoire"
        warning="La suppression de ce mémoire entraînera également la suppression du fichier PDF associé."
        loading={loading}
      />

      <MemoireDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        memoire={currentMemoire}
        onEdit={() => {
          setIsDetailModalOpen(false);
          setTimeout(() => openEditModal(currentMemoire), 300);
        }}
        onDelete={() => {
          setIsDetailModalOpen(false);
          setTimeout(() => openDeleteModal(currentMemoire), 300);
        }}
        onDownload={downloadMemoire}
      />
    </div>
  );
};

export default MemoireManager;